/**
 * JWTトークンをデコードしてパーミッション（スコープ）を取得するユーティリティ
 * 注意: 検証は行わず、読み取りのみを行います（検証はバックエンド側で行います）
 */

/**
 * JWTトークンのペイロード型
 */
interface JwtPayload {
  sub?: string;
  email?: string;
  scope?: string;
  [key: string]: unknown;
}

/**
 * JWTトークンをデコード（検証なし）
 * @param token JWTトークン文字列
 * @returns デコードされたペイロード、またはnull
 */
export const decodeJwtToken = (token: string): JwtPayload | null => {
  try {
    // トークンが空またはnullの場合はnullを返す
    if (!token || typeof token !== 'string') {
      return null;
    }

    // JWTは base64url エンコードされた3つの部分で構成されている: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // ペイロード部分をデコード（base64urlデコード）
    const payload = parts[1];
    if (!payload) {
      return null;
    }

    // base64urlデコード: - を + に、_ を / に変換
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');

    // パディングを追加（必要に応じて）
    const paddingLength = (4 - (base64.length % 4)) % 4;
    const paddedBase64 = base64 + '='.repeat(paddingLength);

    // base64デコード
    const decodedPayload = atob(paddedBase64);
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.error('Failed to decode JWT token:', error);
    return null;
  }
};

/**
 * JWTトークンからスコープ（パーミッション）を取得
 * @param token JWTトークン文字列
 * @returns スコープの配列、または空配列
 */
export const getPermissionsFromToken = (token: string): string[] => {
  const decoded = decodeJwtToken(token);
  if (!decoded || !decoded.scope) {
    return [];
  }

  // スコープはスペース区切りの文字列
  const scopes =
    typeof decoded.scope === 'string' ? decoded.scope.split(' ') : [];

  // openid, profile, email などの標準スコープを除外し、カスタムパーミッションのみを返す
  const customPermissions = scopes.filter((scope: string) => {
    const standardScopes = ['openid', 'profile', 'email', 'offline_access'];
    return !standardScopes.includes(scope);
  });

  return customPermissions;
};

/**
 * 特定のパーミッションを持っているかチェック
 * @param token JWTトークン文字列
 * @param requiredPermissions 必要なパーミッションの配列
 * @returns すべてのパーミッションを持っている場合true
 */
export const hasPermissions = (
  token: string | null,
  requiredPermissions: string[]
): boolean => {
  if (!token || requiredPermissions.length === 0) {
    return false;
  }

  const userPermissions = getPermissionsFromToken(token);
  if (userPermissions.length === 0) {
    return false;
  }

  // 必要なパーミッションがすべて含まれているかチェック
  return requiredPermissions.every((permission) =>
    userPermissions.includes(permission)
  );
};

/**
 * JWTトークンからロール情報を取得
 * Auth0では、ロール情報はカスタムクレームとして含まれることがあります
 * 通常は `https://your-namespace/roles` または `https://your-domain/roles` 形式
 * @param token JWTトークン文字列
 * @param domain Auth0ドメイン（カスタムクレーム名を構築するために使用）
 * @returns ロール名の配列、または空配列
 */
export const getRolesFromToken = (
  token: string | null,
  domain?: string
): string[] => {
  if (!token) {
    return [];
  }

  const decoded = decodeJwtToken(token);
  if (!decoded) {
    return [];
  }

  // Auth0のカスタムクレーム形式を試行
  // 1. https://domain/roles 形式
  // 2. https://domain/role 形式（単数形）
  // 3. roles 形式（直接）
  const possibleClaimNames: string[] = [];

  if (domain) {
    // domainからnamespaceを抽出（例: dev-xxx.auth0.com -> https://dev-xxx.auth0.com）
    const namespace = domain.startsWith('https://')
      ? domain
      : `https://${domain}`;
    possibleClaimNames.push(`${namespace}/roles`);
    possibleClaimNames.push(`${namespace}/role`);
  }

  // 一般的な形式も試行
  possibleClaimNames.push('https://routinify.com/roles');
  possibleClaimNames.push('https://routinify.com/role');
  possibleClaimNames.push('roles');
  possibleClaimNames.push('role');

  // カスタムクレームからロール情報を取得
  for (const claimName of possibleClaimNames) {
    const roles = decoded[claimName];
    if (roles) {
      // 配列の場合はそのまま返す
      if (Array.isArray(roles)) {
        return roles.filter((role): role is string => typeof role === 'string');
      }
      // 文字列の場合は配列に変換
      if (typeof roles === 'string') {
        return [roles];
      }
    }
  }

  return [];
};
